import { createContext, useContext, useMemo } from "react"
import { Box, Button, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { DataGridPremium as DataGrid } from "@mui/x-data-grid-premium"
import type { GridColDef, GridRenderCellParams, GridRowClassNameParams } from "@mui/x-data-grid-premium"
import moment from "moment"
import { capitalize } from "lodash"
import PatSecretField from "components/personal-access-tokens/pat-secret-field"
import type { PatType } from "modules/user-profile/types"
import type { Role } from "model/state"
import type { AccountPatMember, AccountPatToken, PatPendingState } from "../types"
import { patSlotKey } from "../types"

const SCOPE_LABEL: Record<PatType, string> = {
  read: "Read-only",
  read_write: "Read-write",
}

export const TOKEN_LABEL: Record<PatType, string> = {
  read: "read-only token",
  read_write: "read-write token",
}

export const NO_WRITE_ENTITLEMENT_MESSAGE = "Not available — no write entitlement"

// Every member gets both slots, always, whether or not a token exists in them: a member
// holding nothing is exactly the member an admin came here to create a token for, so the
// empty slot has to be visible and actionable rather than absent.
const SLOTS: PatType[] = ["read", "read_write"]

const ROW_HEIGHT = 64

// One injection point for the repeated cell layouts. Per-cell `sx` would pay the runtime
// cost on every cell of every row; these class hooks cost one style injection for the grid.
const GRID_SX = {
  "& .pat-member-group-start": { borderTop: 1, borderColor: "divider" },
  "& .MuiDataGrid-cell--textRight .pat-inline-cell": { justifyContent: "flex-end" },
  "& .pat-stacked-cell": { display: "flex", flexDirection: "column", justifyContent: "center", minWidth: 0 },
  "& .pat-inline-cell": { display: "flex", alignItems: "center", gap: 1, minWidth: 0, width: "100%" },
} satisfies SxProps<Theme>

export interface PatTokenTarget {
  token: AccountPatToken
  memberName: string
}

interface MemberSlotRow {
  id: string
  userId: string
  memberName: string
  memberEmail: string
  memberRole: Role
  /** True on the read slot: the row that carries the member identity for the pair. */
  isGroupStart: boolean
  tokenType: PatType
  token: AccountPatToken | null
  canCreate: boolean
}

interface MemberRowsProps {
  members: AccountPatMember[]
  /** False once the server says tokens are off for the account: every control but Delete is dead. */
  patEnabled: boolean
  pending: PatPendingState
  onCreate: (userId: string, type: PatType) => void
  onCopy: (target: PatTokenTarget) => void
  onRotate: (target: PatTokenTarget) => void
  onRevoke: (target: PatTokenTarget) => void
}

interface SlotState {
  pending: PatPendingState
  patEnabled: boolean
}

const NO_SLOT_STATE: SlotState = {
  pending: { tokenIds: new Set<string>(), creates: new Set<string>() },
  patEnabled: true,
}

// The in-flight slots change on every copy, rotate, revoke and create. Holding them here
// rather than on each row keeps both the `rows` and the `columns` arrays stable, so the grid
// never rebuilds its row model or remounts a cell renderer for a state change that concerns
// one slot. The cells reading this context re-render together on any change — context gives
// no per-cell granularity — but the grid itself stays put.
const SlotStateContext = createContext<SlotState>(NO_SLOT_STATE)

// A filled slot is pending by the id of the token it holds; an empty one by the slot key an
// in-flight create names, so a create on one slot leaves the rest live.
function isSlotPending(pending: PatPendingState, userId: string, tokenType: PatType, token: AccountPatToken | null): boolean {
  if (token) return pending.tokenIds.has(token.id)
  return pending.creates.has(patSlotKey(userId, tokenType))
}

function useSlotState(row: MemberSlotRow) {
  const { pending, patEnabled } = useContext(SlotStateContext)
  const disabled = isSlotPending(pending, row.userId, row.tokenType, row.token)
  return {
    disabled,
    // Create, Copy and Rotate are the three the server rejects while tokens are off, so they
    // are dead rather than merely doomed. Delete keeps `disabled` alone: the server still
    // allows it, and an admin cleaning up after the switch went off needs it.
    mutationDisabled: disabled || !patEnabled,
  }
}

function buildRows(members: AccountPatMember[]): MemberSlotRow[] {
  return members.flatMap(member => SLOTS.map((tokenType, slotIndex) => {
    const token = member.tokens.find(candidate => candidate.tokenType === tokenType) ?? null
    return {
      id: `${member.userId}:${tokenType}`,
      userId: member.userId,
      memberName: member.name,
      memberEmail: member.email,
      memberRole: member.role,
      isGroupStart: slotIndex === 0,
      tokenType,
      token,
      canCreate: tokenType === "read" || member.canCreateReadWrite,
    }
  }))
}

function toTarget(row: MemberSlotRow, token: AccountPatToken): PatTokenTarget {
  return { token, memberName: row.memberName }
}

function tokenLabelFor(row: MemberSlotRow): string {
  return `${TOKEN_LABEL[row.tokenType]} for ${row.memberName}`
}

function MemberCell({ row }: { row: MemberSlotRow }) {
  // The read-write row leaves this cell empty so the pair reads as one member.
  if (!row.isGroupStart) return null

  return <Box className="pat-stacked-cell">
    <Typography variant="body2" noWrap>{row.memberName}</Typography>
    <Typography variant="caption" color="text.secondary" noWrap>
      {`${row.memberEmail} · Account ${capitalize(row.memberRole)}`}
    </Typography>
  </Box>
}

function ScopeCell({ row }: { row: MemberSlotRow }) {
  return <Typography variant="body2">{SCOPE_LABEL[row.tokenType]}</Typography>
}

function TokenCell({ row, onCopy }: { row: MemberSlotRow, onCopy: (target: PatTokenTarget) => void }) {
  const { token } = row
  const { mutationDisabled } = useSlotState(row)

  if (!token) {
    return <Typography variant="body2" color="text.secondary">
      {row.canCreate ? "No token" : NO_WRITE_ENTITLEMENT_MESSAGE}
    </Typography>
  }

  // Copy is the only way a token leaves this page: no `revealedSecret`, no `onToggleReveal`, so
  // the field never draws a plaintext token on an admin's screen. Copy writes it to the
  // clipboard and nothing else, which is all this surface was ever asked to do.
  return <Box className="pat-inline-cell">
    <PatSecretField
      tokenId={token.id}
      prefix={token.prefix}
      disabled={mutationDisabled}
      label={tokenLabelFor(row)}
      onCopy={() => onCopy(toTarget(row, token))}
    />
  </Box>
}

function ActivityCell({ row }: { row: MemberSlotRow }) {
  const { token } = row
  if (!token) return null

  return <Box className="pat-stacked-cell">
    <Typography variant="caption" color="text.secondary">
      {`Created ${moment(token.createdAt).format("ll")}`}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {token.lastUsedAt ? `Used ${moment(token.lastUsedAt).fromNow()}` : "Never used"}
    </Typography>
  </Box>
}

interface ActionsCellProps {
  row: MemberSlotRow
  onCreate: (userId: string, type: PatType) => void
  onRotate: (target: PatTokenTarget) => void
  onRevoke: (target: PatTokenTarget) => void
}

function ActionsCell({ row, onCreate, onRotate, onRevoke }: ActionsCellProps) {
  const { token } = row
  const { disabled, mutationDisabled } = useSlotState(row)

  if (!token) {
    if (!row.canCreate) return null
    return <Button
      size="small"
      variant="outlined"
      aria-label={`Create ${tokenLabelFor(row)}`}
      disabled={mutationDisabled}
      onClick={() => onCreate(row.userId, row.tokenType)}
    >
      Create
    </Button>
  }

  const label = tokenLabelFor(row)

  return <Box className="pat-inline-cell">
    <Button size="small" aria-label={`Rotate ${label}`} disabled={mutationDisabled} onClick={() => onRotate(toTarget(row, token))}>
      Rotate
    </Button>
    <Button size="small" color="error" aria-label={`Delete ${label}`} disabled={disabled} onClick={() => onRevoke(toTarget(row, token))}>
      Delete
    </Button>
  </Box>
}

function getRowClassName({ row }: GridRowClassNameParams<MemberSlotRow>) {
  return row.isGroupStart ? "pat-member-group-start" : ""
}

export default function MemberRows({
  members,
  patEnabled,
  pending,
  onCreate,
  onCopy,
  onRotate,
  onRevoke,
}: MemberRowsProps) {
  const rows = useMemo(() => buildRows(members), [members])

  const slotState = useMemo<SlotState>(() => ({ pending, patEnabled }), [pending, patEnabled])

  // Sorting is off on every column: the two slots of a member are adjacent by construction,
  // and a re-sort would scatter them and strand the member identity on an unrelated row.
  // `display: "flex"` centres each cell's content in the row height — without it the grid
  // leaves it at the top, which reads as misaligned next to the two-line cells.
  const columns = useMemo<GridColDef<MemberSlotRow>[]>(() => [
    {
      field: "memberName",
      headerName: "Member",
      flex: 2,
      minWidth: 220,
      display: "flex",
      sortable: false,
      renderCell: ({ row }: GridRenderCellParams<MemberSlotRow>) => <MemberCell row={row} />,
    },
    {
      field: "tokenType",
      headerName: "Scope",
      width: 120,
      display: "flex",
      sortable: false,
      renderCell: ({ row }: GridRenderCellParams<MemberSlotRow>) => <ScopeCell row={row} />,
    },
    {
      field: "token",
      headerName: "Token",
      flex: 2,
      minWidth: 260,
      display: "flex",
      sortable: false,
      renderCell: ({ row }: GridRenderCellParams<MemberSlotRow>) => <TokenCell row={row} onCopy={onCopy} />,
    },
    {
      field: "activity",
      headerName: "Activity",
      flex: 1,
      minWidth: 180,
      display: "flex",
      sortable: false,
      renderCell: ({ row }: GridRenderCellParams<MemberSlotRow>) => <ActivityCell row={row} />,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 260,
      display: "flex",
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }: GridRenderCellParams<MemberSlotRow>) => <ActionsCell
        row={row}
        onCreate={onCreate}
        onRotate={onRotate}
        onRevoke={onRevoke}
      />,
    },
  ], [onCopy, onCreate, onRotate, onRevoke])

  return <SlotStateContext.Provider value={slotState}>
    <DataGrid
      autoHeight
      rows={rows}
      columns={columns}
      rowHeight={ROW_HEIGHT}
      getRowClassName={getRowClassName}
      disableRowSelectionOnClick
      disableColumnFilter
      disableColumnMenu
      disableColumnSelector
      disableColumnResize
      hideFooter
      sx={GRID_SX}
    />
  </SlotStateContext.Provider>
}
